import React, { useState } from "react"
import { GripVertical, Pencil, Trash2, Check, X } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Badge } from "src/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"

const KIND_LABELS = { open: "Open", won: "Won", lost: "Lost" }
const KIND_COLORS = { open: "bg-blue-100 text-blue-700", won: "bg-emerald-100 text-emerald-700", lost: "bg-red-100 text-red-700" }

export function StageRow({ stage, onUpdate, onDelete, isUpdating, isDeleting, dragHandleProps, canManage = true }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(stage.name)
  const [kind, setKind] = useState(stage.kind || "open")
  const [probability, setProbability] = useState(stage.probability ?? "")
  const [color, setColor] = useState(stage.color || "#94a3b8")

  const handleSave = async () => {
    try {
      await onUpdate(stage.id, { name, kind, probability: probability === "" ? null : Number(probability), color })
      setEditing(false)
    } catch { /* toast handled upstream */ }
  }

  const handleCancel = () => {
    setName(stage.name)
    setKind(stage.kind || "open")
    setProbability(stage.probability ?? "")
    setColor(stage.color || "#94a3b8")
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/50 px-3 py-2.5">
        <div className="w-5" />
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-40" placeholder="Stage name" />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-8 w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number" min="0" max="100" value={probability}
          onChange={(e) => setProbability(e.target.value)}
          className="h-8 w-20" placeholder="%"
        />
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-8 w-8 cursor-pointer rounded border" />
        <Button size="sm" variant="ghost" onClick={handleSave} disabled={isUpdating || !name.trim()}>
          <Check className="h-4 w-4 text-emerald-600" />
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel}>
          <X className="h-4 w-4 text-slate-400" />
        </Button>
      </div>
    )
  }

  return (
    <div className="group flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2.5 transition-colors hover:border-slate-200 hover:bg-slate-50/50">
      <span {...dragHandleProps} className="cursor-grab text-slate-300 hover:text-slate-500">
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: stage.color || "#94a3b8" }} />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{stage.name}</span>
      <Badge variant="outline" className={`text-[11px] ${KIND_COLORS[stage.kind] || ""}`}>
        {KIND_LABELS[stage.kind] || stage.kind}
      </Badge>
      {stage.probability != null && (
        <span className="text-xs text-slate-400">{stage.probability}%</span>
      )}
      {canManage && (
        <div className="flex gap-0.5">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(true)} title="Edit stage">
            <Pencil className="h-3.5 w-3.5 text-slate-400" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDelete(stage.id)} disabled={isDeleting} title="Delete stage">
            <Trash2 className="h-3.5 w-3.5 text-red-400" />
          </Button>
        </div>
      )}
    </div>
  )
}
